import { useState } from 'react'
import { mdiCheck, mdiClose } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useCampaignChanges, useDecideChange, type Campaign, type CampaignChange } from '../../hooks/useCampaigns'
import { SECTION_ICONS } from '../../lib/section-icons'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { AppIcon } from '../ui/icon'
import { Skeleton } from '../ui/skeleton'
import { APPLY_STATUS } from './campaign-status'
import { StatusPill } from './StatusPill'

function show(value: unknown): string {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'string') return value
    return JSON.stringify(value)
}

/**
 * The values that contradict the patrimoine, accepted or refused one at a
 * time (patterns §32, review a diff then apply): the agent's answer, the value
 * known at the launch and today's value side by side.
 */
export function CampaignChangesTab({ org, campaign }: { org: string; campaign: Campaign }) {
    const { t } = useTranslation('campaigns')
    const { data: changes, isLoading } = useCampaignChanges(org, campaign.uid, campaign.status !== 'draft')
    if (campaign.status === 'draft') {
        return <EmptyState icon={SECTION_ICONS.campaigns} title={t('units_draft_title')} description={t('units_draft_description')} />
    }
    if (isLoading) return <Skeleton className="h-32 w-full" />
    if (!changes || changes.length === 0) {
        return <EmptyState icon={SECTION_ICONS.campaigns} title={t('changes.empty')} data-testid="campaign-changes-empty" />
    }
    return (
        <div className="space-y-3" data-testid="campaign-changes">
            <p className="text-sm text-muted-foreground">
                {campaign.status === 'closed' ? t('changes.hint_closed') : t('changes.hint_launched')}
            </p>
            <ul className="divide-y rounded-lg border">
                {changes.map((c) => (
                    <ChangeRow key={c.uid} org={org} campaign={campaign} change={c} />
                ))}
            </ul>
        </div>
    )
}

function ChangeRow({ org, campaign, change }: { org: string; campaign: Campaign; change: CampaignChange }) {
    const { t, i18n } = useTranslation('campaigns')
    const lang = i18n.language.slice(0, 2)
    const decide = useDecideChange(org, campaign.uid)
    const [error, setError] = useState<string | null>(null)
    const run = (decision: 'accept' | 'reject') =>
        decide.mutateAsync({ entryUid: change.uid, decision }).then(() => setError(null), (e: Error) => setError(e.message))
    const closed = campaign.status === 'closed'
    const canAccept = closed
        ? ['conflict', 'failed', 'rejected'].includes(change.apply_status)
        : change.apply_status === 'rejected'
    const canReject = change.apply_status !== 'applied' && change.apply_status !== 'rejected'
    const where = change.asset.uid === change.unit.uid ? change.unit.name : `${change.unit.name} · ${change.asset.name}`
    return (
        <li className="space-y-2 p-3" data-testid={`campaign-change-${change.uid}`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{change.label[lang] || change.label.fr || change.label.en || Object.values(change.label)[0]}</span>
                <span className="text-sm text-muted-foreground">{where}</span>
                <StatusPill map={APPLY_STATUS} family="apply_status" value={change.apply_status} testId={`campaign-change-${change.uid}-status`} />
            </div>
            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                <div>
                    <dt className="text-xs text-muted-foreground">{t('changes.known')}</dt>
                    <dd>{show(change.known_value)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted-foreground">{t('changes.current')}</dt>
                    <dd>{show(change.current_value)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted-foreground">
                        {t('changes.answer')}
                        {change.answered_by ? ` (${change.answered_by})` : ''}
                    </dt>
                    <dd className="font-medium">{show(change.answer)}</dd>
                </div>
            </dl>
            {change.apply_error && <p className="text-sm text-destructive">{change.apply_error}</p>}
            {(canAccept || canReject) && (
                <div className="flex gap-2">
                    {canAccept && (
                        <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => void run('accept')} data-testid={`campaign-change-${change.uid}-accept`}>
                            <AppIcon path={mdiCheck} size="sm" className="mr-1" />
                            {closed && change.apply_status !== 'rejected' ? t('changes.write') : t('changes.accept')}
                        </Button>
                    )}
                    {canReject && (
                        <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => void run('reject')} data-testid={`campaign-change-${change.uid}-reject`}>
                            <AppIcon path={mdiClose} size="sm" className="mr-1" />
                            {t('changes.reject')}
                        </Button>
                    )}
                </div>
            )}
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        </li>
    )
}
